import { type JSX, useEffect, useState } from 'react';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getAllOrganizationsWithBasicData } from 'Api/UcrmTprm/UcrmTprmApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { DataGrid, GridColumn, dateColumn } from 'Components/DataGrid/DataGrid';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { EXPORTS } from 'Config/Paths';
import { getDepartmentFromSubject, getUserNameFromSubject } from 'Helpers/UserUtils';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ThirdParty, ThirdPartyContact } from 'Models/TPRM';
import { Business } from 'Models/Ucrm';

/** @param organization_manager_department Derived from the organization manager's user ID; it is not a field in the organization model that comes from the API. */
interface OrganizationExportRow extends ThirdParty {
    organization_manager_department: string;
}

/** Columns that are omitted from the UCRM export, since businesses do not have these fields. */
const TPRM_ONLY_FIELDS: (keyof OrganizationExportRow)[] = ['website', 'ein', 'country', 'address_line_1', 'address_line_2', 'city', 'state', 'zip_code', 'vendor_contacts'];

export interface OrganizationsExportProps {
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;

    disableVirtualization?: boolean;
}

/** Exports TPRM third parties or UCRM businesses, depending on `module`. */
export const OrganizationsExport = (props: OrganizationsExportProps): JSX.Element => {
    const [organizations, setOrganizations] = useState<(ThirdParty | Business)[]>();
    const [error, setError] = useState<string>();
    const cachedData = useCachedData();

    // Save the Tags map locally so it can be repeatedly accessed rather than recreating it as the rows and Tags are looped through.
    const tagsMap = cachedData.getTagsMap();

    useEffect(() => {
        const fetchOrganizations = async (): Promise<void> => {
            try {
                const organizationsResponse = await getAllOrganizationsWithBasicData(props.moduleApi);
                setOrganizations(organizationsResponse.data);
            } catch (error) {
                setError(error.message);
            }
        };

        fetchOrganizations();
    }, [props.moduleApi]);

    const isUcrm = props.module === ProductModule.UCRM;
    const organizationTerm = isUcrm ? 'Business' : 'Third-Party';
    const pluralOrganizationTerm = isUcrm ? 'Businesses' : 'Third Parties';

    const allColumns: GridColumn<OrganizationExportRow>[] = [
        { field: 'name', headerName: `${organizationTerm} Name`, width: 300 },
        { field: 'website', headerName: 'Website', width: 300 },
        { field: 'ein', headerName: 'Employer ID number', width: 300 },
        { field: 'country', headerName: 'Country/Region', width: 300 },
        { field: 'address_line_1', headerName: 'Address line 1', width: 300 },
        { field: 'address_line_2', headerName: 'Address line 2', width: 300 },
        { field: 'city', headerName: 'City', width: 300 },
        { field: 'state', headerName: 'State/territory', width: 300 },
        { field: 'zip_code', headerName: 'Zip Code', width: 300 },
        {
            field: 'tags',
            headerName: 'Tags',
            width: 300,
            valueGetter: (_value, row) =>
                row.tags
                    .sort()
                    .map((tagId: string) => tagsMap.get(tagId)?.title ?? tagId)
                    .join(', '),
        },
        {
            field: 'vendor_contacts',
            headerName: 'Third-Party Contacts',
            width: 300,
            valueGetter: (value) =>
                value
                    // Only show the name for each contact. Omit their email address and phone number.
                    .filter((thirdPartyContact: ThirdPartyContact) => thirdPartyContact.name !== undefined)
                    .sort((thirdPartyContactA: ThirdPartyContact, thirdPartyContactB: ThirdPartyContact) => (thirdPartyContactA?.name || '').localeCompare(thirdPartyContactB?.name || ''))
                    .map((thirdPartyContact: ThirdPartyContact) => thirdPartyContact.name)
                    .join(', '),
        },
        { field: 'organization_manager_department', headerName: `${organizationTerm} Manager Department`, width: 300, valueGetter: (_value, row) => getDepartmentFromSubject(row.vendor_manager_user_id, cachedData.users) },
        { field: 'vendor_manager_user_id', headerName: `${organizationTerm} Manager`, width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        dateColumn('created_time', 'Created Date'),
        { field: 'created_by', headerName: 'Created By', width: 300, valueGetter: (value) => (value === undefined ? undefined : getUserNameFromSubject(value, cachedData.users)) },
        dateColumn('finalized_time', 'Finalized Date'),
        { field: 'finalized_by', headerName: 'Finalized By', width: 300, valueGetter: (value) => (value === undefined ? undefined : getUserNameFromSubject(value, cachedData.users)) },
        { field: 'finalization_state', headerName: 'Finalization State', width: 300 },
        { field: 'active_state', headerName: 'Active State', width: 300 },
        { field: 'id', headerName: 'ID', width: 300 },
    ];
    const columns = isUcrm ? allColumns.filter((column) => !TPRM_ONLY_FIELDS.includes(column.field as keyof OrganizationExportRow)) : allColumns;

    if (organizations) {
        return (
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={`/${EXPORTS}`}>Exports</BreadcrumbLink>
                        <BreadcrumbText>{pluralOrganizationTerm}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={`${pluralOrganizationTerm} Export`}
                body={[{ content: <DataGrid columns={columns} getRowId={(row: OrganizationExportRow) => row.id} rows={organizations} title={`${pluralOrganizationTerm} Export Preview`} fileName={`${pluralOrganizationTerm} Export`} disableVirtualization={props.disableVirtualization} /> }]}
            />
        );
    } else if (error) {
        return <Text color="darkBlue">{error}</Text>;
    } else return <Placeholder />;
};
